import React, { useState, useEffect, useRef, useMemo } from 'react';
import { auth } from '../../services/api';

const GlobalStyles = () => (
  <style>{`
    .utility-modal-input[type="number"]::-webkit-outer-spin-button,
    .utility-modal-input[type="number"]::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .utility-modal-input[type="number"] {
      -moz-appearance: textfield;
    }
  `}</style>
);

interface DropdownOption {
  value: string;
  display_name: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const dropdownStyles = {
  container: {
    position: 'relative' as const,
    width: '100%',
  },
  trigger: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    backgroundColor: '#fff',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  triggerDisabled: {
    backgroundColor: '#f8fafc',
    color: '#64748b',
    cursor: 'not-allowed',
  },
  triggerOpen: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  },
  placeholder: {
    color: '#94a3b8',
  },
  arrow: {
    width: '1rem',
    height: '1rem',
    color: '#64748b',
    transition: 'transform 0.2s',
  },
  arrowOpen: {
    transform: 'rotate(180deg)',
  },
  option: {
    padding: '0.5rem 0.75rem',
    fontSize: '0.8125rem',
    color: '#1e293b',
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  optionHover: {
    backgroundColor: '#f1f5f9',
  },
  optionSelected: {
    backgroundColor: '#eff6ff',
    color: '#3b82f6',
  },
};

const CustomDropdown: React.FC<CustomDropdownProps> = ({ options, value, onChange, placeholder = 'Select...', disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} style={dropdownStyles.container}>
      <div
        ref={triggerRef}
        style={{
          ...dropdownStyles.trigger,
          ...(disabled ? dropdownStyles.triggerDisabled : {}),
          ...(isOpen ? dropdownStyles.triggerOpen : {}),
        }}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span style={!selectedOption ? dropdownStyles.placeholder : undefined}>
          {selectedOption ? selectedOption.display_name : placeholder}
        </span>
        <svg
          style={{ ...dropdownStyles.arrow, ...(isOpen ? dropdownStyles.arrowOpen : {}) }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          width: menuPosition.width,
          backgroundColor: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '0.375rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 9999,
          maxHeight: '12rem',
          overflowY: 'auto' as const,
        }}>
          {options.map((option, index) => (
            <div
              key={option.value}
              style={{
                ...dropdownStyles.option,
                ...(hoveredIndex === index ? dropdownStyles.optionHover : {}),
                ...(value === option.value ? dropdownStyles.optionSelected : {}),
              }}
              onClick={() => handleSelect(option.value)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {option.display_name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface UtilityAccount {
  id?: number;
  property_id: number;
  utility_configuration_id?: number;
  utility_type: string;
  provider_name: string;
  account_number: string;
  procurement_number?: string;
  annual_budget?: number | string;
  billing_frequency?: string;
  billing_day?: number;
  active: boolean;
}

interface UtilityType {
  value: string;
  display_name: string;
}

interface Provider {
  id: number;
  provider_name: string;
  utility_type: string;
}

interface UtilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: number;
  utilityAccountId?: number;
  mode: 'add' | 'edit';
  onSuccess?: () => void;
  property?: any;
}

const styles: { [key: string]: React.CSSProperties } = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '0.75rem',
    width: '64rem',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  title: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#111827',
  },
  statusToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  toggle: {
    width: '2.5rem',
    height: '1.25rem',
    borderRadius: '0.625rem',
    cursor: 'pointer',
    position: 'relative' as const,
    transition: 'background-color 0.2s',
  },
  toggleOn: {
    backgroundColor: '#3b82f6',
  },
  toggleOff: {
    backgroundColor: '#d1d5db',
  },
  toggleSlider: {
    position: 'absolute' as const,
    width: '1rem',
    height: '1rem',
    backgroundColor: '#fff',
    borderRadius: '50%',
    top: '0.125rem',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleSliderOn: {
    left: '1.375rem',
  },
  toggleSliderOff: {
    left: '0.125rem',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    color: '#6b7280',
    borderRadius: '0.25rem',
  },
  body: {
    padding: '1.5rem',
    overflowY: 'auto' as const,
    maxHeight: 'calc(90vh - 8rem)',
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.5rem',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#f0f9ff',
    borderBottom: '1px solid #e0f2fe',
  },
  cardHeaderIcon: {
    width: '1.25rem',
    height: '1.25rem',
    color: '#0284c7',
  },
  cardTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#0284c7',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  cardBody: {
    padding: '1rem',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
    marginBottom: '1rem',
  },
  formRowSingle: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',
    marginBottom: '1rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
  },
  label: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '0.375rem',
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb',
  },
  btnCancel: {
    padding: '0.5rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: '#fff',
    color: '#374151',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSave: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  btnSaveDisabled: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    backgroundColor: '#9ca3af',
    color: '#fff',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'not-allowed',
  },
  errorMessage: {
    color: '#dc2626',
    margin: '0 0 1rem 0',
    textAlign: 'center' as const,
    padding: '0.75rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '0.375rem',
  },
};

const UtilityModal: React.FC<UtilityModalProps> = ({
  isOpen,
  onClose,
  propertyId,
  utilityAccountId,
  mode,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<UtilityAccount>({
    property_id: propertyId,
    utility_type: '',
    provider_name: '',
    account_number: '',
    procurement_number: '',
    annual_budget: undefined,
    billing_frequency: 'monthly',
    billing_day: undefined,
    active: true
  });

  const [loading, setLoading] = useState(false);
  const [utilityTypes, setUtilityTypes] = useState<UtilityType[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<DropdownOption[]>([]);
  const [billingFrequencyOptions, setBillingFrequencyOptions] = useState<UtilityType[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [originalFormData, setOriginalFormData] = useState<UtilityAccount | null>(null);

  const isDirty = useMemo(() => {
    if (!originalFormData) return mode === 'add';
    const normalize = (val: unknown) => (val === null || val === undefined || val === '') ? '' : String(val);
    const keys = Object.keys(formData) as (keyof UtilityAccount)[];
    return keys.some(key => normalize(formData[key]) !== normalize(originalFormData?.[key]));
  }, [formData, mode, originalFormData]);

  useEffect(() => {
    if (isOpen) {
      fetchUtilityTypes();
      fetchProviders();
      fetchBillingFrequencyOptions();
      if (mode === 'edit' && utilityAccountId) {
        fetchUtilityAccount();
      } else {
        resetForm();
      }
    }
  }, [isOpen, mode, utilityAccountId]);

  useEffect(() => {
    if (formData.utility_type && providers.length > 0) {
      const filtered = providers
        .filter(p => p.utility_type.toLowerCase() === formData.utility_type.toLowerCase() && !p.provider_name.toLowerCase().includes('unknown'))
        .map(p => ({ value: p.provider_name, display_name: p.provider_name }));
      setFilteredProviders(filtered);
    } else {
      setFilteredProviders([]);
    }
  }, [formData.utility_type, providers]);

  const fetchUtilityTypes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/lookup-values?category=utility_type`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        setUtilityTypes(await response.json());
      }
    } catch (error) {
      console.error('Error fetching utility types:', error);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/providers`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        setProviders(await response.json());
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchBillingFrequencyOptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/lookup-values?category=billing_frequency`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        setBillingFrequencyOptions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching billing frequency options:', error);
    }
  };

  const fetchUtilityAccount = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/utility-accounts/${utilityAccountId}`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
        setOriginalFormData({ ...data });
      }
    } catch (error) {
      console.error('Error fetching utility account:', error);
    }
  };

  const resetForm = () => {
    const emptyForm: UtilityAccount = {
      property_id: propertyId,
      utility_type: '',
      provider_name: '',
      account_number: '',
      procurement_number: '',
      annual_budget: undefined,
      billing_frequency: 'monthly',
      billing_day: undefined,
      active: true
    };
    setFormData(emptyForm);
    setOriginalFormData(null);
    setErrors({});
    setSuccessMessage('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    if (name === 'annual_budget') {
      const cleanValue = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
      setFormData(prev => ({ ...prev, [name]: cleanValue === '' ? undefined : cleanValue }));
    } else if (name === 'billing_day') {
      const cleanValue = value.replace(/[^\d]/g, '');
      const numValue = cleanValue === '' ? undefined : Math.min(31, Math.max(1, parseInt(cleanValue)));
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleProviderChange = (providerName: string) => {
    const selectedProvider = providers.find(p => p.provider_name === providerName);
    setFormData(prev => ({
      ...prev,
      provider_name: providerName,
      utility_configuration_id: selectedProvider?.id
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const url = mode === 'add'
        ? `${process.env.REACT_APP_API_URL}/api/admin/properties/${propertyId}/utility-accounts`
        : `${process.env.REACT_APP_API_URL}/api/admin/utility-accounts/${utilityAccountId}`;

      const response = await fetch(url, {
        method: mode === 'add' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', ...auth.getAuthHeaders() },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setOriginalFormData({ ...formData });
        setSuccessMessage('saved');
        setTimeout(() => {
          setSuccessMessage('');
          onSuccess?.();
        }, 3000);
      } else {
        setErrors({ submit: 'Failed to save utility account. Please try again.' });
      }
    } catch (error) {
      console.error('Error saving utility account:', error);
      setErrors({ submit: 'Failed to save utility account. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const Toggle = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <div
      style={{ ...styles.toggle, ...(checked ? styles.toggleOn : styles.toggleOff) }}
      onClick={onChange}
    >
      <div style={{ ...styles.toggleSlider, ...(checked ? styles.toggleSliderOn : styles.toggleSliderOff) }} />
    </div>
  );

  return (
    <div style={styles.backdrop}>
      <GlobalStyles />
      <div style={styles.modal}>
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h2 style={styles.title}>{mode === 'add' ? 'Add Utility Account' : 'Edit Utility Account'}</h2>
            {mode === 'edit' && (
              <div style={styles.statusToggle}>
                <span>Status:</span>
                <Toggle checked={formData.active || false} onChange={() => setFormData(prev => ({ ...prev, active: !prev.active }))} />
                <span>{formData.active ? 'Active' : 'Inactive'}</span>
              </div>
            )}
          </div>
          {successMessage && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.25rem 0.75rem',
              backgroundColor: '#dcfce7',
              color: '#16a34a',
              borderRadius: '1rem',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Record Saved Successfully
            </div>
          )}
          <button style={styles.closeBtn} onClick={onClose} disabled={loading}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {errors.submit && <div style={styles.errorMessage}>{errors.submit}</div>}

            <div style={styles.cardsRow}>
              {/* Account Information Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <svg style={styles.cardHeaderIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span style={styles.cardTitle}>Account Information</span>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.formRow}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Utility Type <span style={{ color: '#ef4444' }}>*</span></label>
                      <CustomDropdown
                        options={utilityTypes}
                        value={formData.utility_type}
                        onChange={(val) => setFormData(prev => ({ ...prev, utility_type: val }))}
                        placeholder="Select Utility Type"
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Provider <span style={{ color: '#ef4444' }}>*</span></label>
                      <CustomDropdown
                        options={filteredProviders}
                        value={formData.provider_name}
                        onChange={handleProviderChange}
                        placeholder="Select Provider"
                        disabled={loading || !formData.utility_type}
                      />
                    </div>
                  </div>
                  <div style={{ ...styles.formRow, marginBottom: 0 }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Account Number <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        name="account_number"
                        value={formData.account_number}
                        onChange={handleInputChange}
                        style={styles.input}
                        required
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Procurement Number</label>
                      <input
                        type="text"
                        name="procurement_number"
                        value={formData.procurement_number || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Billing Information Card */}
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <svg style={styles.cardHeaderIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span style={styles.cardTitle}>Billing Information</span>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.formRowSingle}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Annual Budget ($)</label>
                      <input
                        type="number"
                        name="annual_budget"
                        value={formData.annual_budget || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        className="utility-modal-input"
                        min="0"
                        step="0.01"
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div style={{ ...styles.formRow, marginBottom: 0 }}>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Billing Frequency</label>
                      <CustomDropdown
                        options={billingFrequencyOptions}
                        value={formData.billing_frequency || ''}
                        onChange={(val) => setFormData(prev => ({ ...prev, billing_frequency: val }))}
                        placeholder="Select Frequency"
                        disabled={loading}
                      />
                    </div>
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Billing Day</label>
                      <input
                        type="number"
                        name="billing_day"
                        value={formData.billing_day || ''}
                        onChange={handleInputChange}
                        style={styles.input}
                        className="utility-modal-input"
                        min="1"
                        max="31"
                        placeholder="1-31"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            <button type="button" style={styles.btnCancel} onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              type="submit"
              style={isDirty && !loading ? styles.btnSave : styles.btnSaveDisabled}
              disabled={loading || !isDirty}
            >
              {loading ? 'Saving...' : mode === 'add' ? 'Create Utility Account' : 'Update Utility Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UtilityModal;
